/* assets/gtender-ios-hig.js — GTENDER-IOS-HIG-RUNTIME-11-VISIBILITY-A11Y
 * Owner: responsive HIG interaction projection for /pages/gtender only.
 * Boundary: consumes existing GTender search/detail contracts only.
 * Bookmark labels, persistence, sharing and haptic feedback remain owned by
 * their canonical GTender runtimes.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-gtender-public-entry]';
  var VERSION = '2026-10-17-ios-hig-25-gtender-map-route';
  var DESKTOP_FILTER_QUERY = '(min-width: 1024px)';
  var MOBILE_FILTER_QUERY = '(max-width: 767px)';
  var MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
  var filterOpenerByRoot = new WeakMap();
  var mobileMapSheetByRoot = new WeakMap();

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function isZh(root) { return /^zh/i.test(clean(root && root.getAttribute('data-locale')) || clean(document.documentElement.lang)); }
  function two(value) { return String(value).padStart(2, '0'); }
  function usesDesktopFilterSidebar() { return Boolean(window.matchMedia && window.matchMedia(DESKTOP_FILTER_QUERY).matches); }
  function usesMobileFilterListbox() { return Boolean(window.matchMedia && window.matchMedia(MOBILE_FILTER_QUERY).matches); }

  function applyChipActiveStyle(chip, active) {
    if (!chip) return;
    if (active) {
      chip.style.background = 'var(--lg-form-accent, #174479)';
      chip.style.borderColor = 'var(--lg-form-accent, #174479)';
      chip.style.color = '#fff';
    } else {
      chip.style.removeProperty('background');
      chip.style.removeProperty('border-color');
      chip.style.removeProperty('color');
    }
  }

  function applyFilterBadgeStyle(filterChip, countLabel, count, activeCount) {
    if (!filterChip || !countLabel || !count) return;
    filterChip.style.position = 'relative';
    countLabel.style.position = 'absolute';
    countLabel.style.insetInlineEnd = '-4px';
    countLabel.style.top = '-4px';
    countLabel.style.display = activeCount > 0 ? 'grid' : 'none';
    countLabel.style.placeItems = 'center';
    countLabel.style.width = '18px';
    countLabel.style.height = '18px';
    countLabel.style.margin = '0';
    countLabel.style.borderRadius = '999px';
    countLabel.style.background = activeCount > 0 ? '#fff' : 'transparent';
    countLabel.style.color = 'var(--lg-form-accent, #174479)';
    countLabel.style.fontSize = '0';
    countLabel.style.lineHeight = '1';
    countLabel.style.boxShadow = activeCount > 0 ? '0 0 0 1px var(--lg-form-accent, #174479)' : 'none';
    count.style.fontSize = '10px';
    count.style.fontWeight = '800';
  }

  function selectedControlLabel(control) {
    if (!control) return '';
    var selected = control.selectedOptions && control.selectedOptions[0];
    return clean(selected && selected.textContent) || clean(control.value);
  }

  function filterName(root, key) {
    var labels = isZh(root)
      ? (/^zh-CN$/i.test(clean(root.getAttribute('data-locale')))
        ? { category: '分类', department: '发布机构', status: '状态', deadline: '截止日期', 'institution-type': '机构类型', source: '数据来源' }
        : { category: '分類', department: '發佈機構', status: '狀態', deadline: '截止日期', 'institution-type': '機構類型', source: '資料來源' })
      : { category: 'Category', department: 'Issuing organisation', status: 'Status', deadline: 'Closing date', 'institution-type': 'Organisation type', source: 'Data source' };
    return labels[key] || key;
  }

  function activeStructuredFilters(root) {
    var quickClosing = root.getAttribute('data-gtender-closing-quick') === 'true';
    var entries = [];
    ['category', 'department', 'status', 'deadline', 'institution-type', 'source'].forEach(function (key) {
      var control = root.querySelector('[data-gtender-search-' + key + ']');
      var value = clean(control && control.value);
      if (!value || (key === 'status' && quickClosing && value === 'closing')) return;
      entries.push({ key: key, label: filterName(root, key) + ': ' + selectedControlLabel(control) });
    });
    return entries;
  }

  function mobileMapSheetState(root) {
    var state = mobileMapSheetByRoot.get(root);
    if (state) return state;
    state = {
      detent: 'peek',
      dragging: false,
      pointerId: null,
      startY: 0,
      startOffset: 0,
      lastY: 0,
      lastTime: 0,
      velocity: 0,
      offset: 0,
      geometry: null,
      boundMeta: null,
      listColumn: null
    };
    mobileMapSheetByRoot.set(root, state);
    return state;
  }

  function mobileMapSheetGeometry(stage) {
    var rect = stage && stage.getBoundingClientRect ? stage.getBoundingClientRect() : null;
    var fallback = Math.min(Math.max(window.innerHeight * 0.72, 560), 760);
    var stageHeight = Math.max(1, Math.round((rect && rect.height) || stage.clientHeight || fallback));
    var fullHeight = Math.max(1, Math.round(stageHeight * 0.90));
    var peekVisible = Math.max(144, Math.round(stageHeight * 0.20));
    var halfVisible = Math.round(stageHeight * 0.50);
    return {
      fullHeight: fullHeight,
      offsets: {
        full: 0,
        half: Math.max(0, fullHeight - halfVisible),
        peek: Math.max(0, fullHeight - peekVisible)
      }
    };
  }

  function clampMobileMapSheetOffset(value, geometry) {
    if (!geometry) return 0;
    return Math.max(geometry.offsets.full, Math.min(geometry.offsets.peek, value));
  }

  function mobileMapSheetReducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function setMobileMapSheetOffset(listColumn, state, offset, animate) {
    if (!listColumn || !state || !state.geometry) return;
    state.offset = clampMobileMapSheetOffset(offset, state.geometry);
    listColumn.style.transition = animate
      ? (mobileMapSheetReducedMotion()
          ? 'transform 0.01ms linear'
          : 'transform 360ms cubic-bezier(0.32, 0.72, 0, 1)')
      : 'none';
    listColumn.style.transform = 'translate3d(0,' + Math.round(state.offset) + 'px,0)';
  }

  function nearestMobileMapSheetDetent(state) {
    if (!state || !state.geometry) return 'peek';
    var names = ['full', 'half', 'peek'];
    var nearest = names[0];
    var distance = Infinity;
    names.forEach(function (name) {
      var next = Math.abs(state.offset - state.geometry.offsets[name]);
      if (next < distance) { distance = next; nearest = name; }
    });
    return nearest;
  }

  function releaseMobileMapSheetDetent(state) {
    var names = ['full', 'half', 'peek'];
    var nearest = nearestMobileMapSheetDetent(state);
    var index = Math.max(0, names.indexOf(nearest));
    if (state.velocity <= -0.45) return names[Math.max(0, index - 1)];
    if (state.velocity >= 0.45) return names[Math.min(names.length - 1, index + 1)];
    return nearest;
  }

  function clearMobileMapSheetStyles(listColumn, meta, summary) {
    if (listColumn) {
      ['display', 'position', 'inset', 'z-index', 'box-sizing', 'width', 'height', 'max-height', 'margin', 'padding', 'overflow', 'overflow-y', 'overscroll-behavior', '-webkit-overflow-scrolling', 'border', 'border-radius', 'background', 'box-shadow', 'backdrop-filter', '-webkit-backdrop-filter', 'transform', 'transition'].forEach(function (name) {
        listColumn.style.removeProperty(name);
      });
    }
    if (meta) {
      ['position', 'top', 'z-index', 'margin', 'padding', 'background', 'background-image', 'background-repeat', 'background-position', 'background-size', 'box-shadow', 'backdrop-filter', '-webkit-backdrop-filter', 'touch-action', 'cursor', 'border-radius'].forEach(function (name) {
        meta.style.removeProperty(name);
      });
    }
    if (summary) summary.style.removeProperty('display');
  }

  function bindMobileMapSheet(root, meta, listColumn) {
    var state = mobileMapSheetState(root);
    state.listColumn = listColumn;
    if (!meta || state.boundMeta === meta) return;
    state.boundMeta = meta;

    function begin(event) {
      if (!usesMobileFilterListbox() || root.getAttribute('data-gtender-view-mode') !== 'map') return;
      if (!state.geometry || !state.listColumn || state.listColumn.parentElement === null) return;
      if (event.button != null && event.button !== 0) return;
      state.dragging = true;
      state.pointerId = event.pointerId;
      state.startY = event.clientY;
      state.startOffset = state.offset;
      state.lastY = event.clientY;
      state.lastTime = Number(event.timeStamp) || Date.now();
      state.velocity = 0;
      state.listColumn.style.transition = 'none';
      meta.style.cursor = 'grabbing';
      if (meta.setPointerCapture && event.pointerId != null) {
        try { meta.setPointerCapture(event.pointerId); } catch (_) {}
      }
      event.preventDefault();
    }

    function move(event) {
      if (!state.dragging || (state.pointerId != null && event.pointerId !== state.pointerId)) return;
      var now = Number(event.timeStamp) || Date.now();
      var elapsed = Math.max(1, now - state.lastTime);
      state.velocity = (event.clientY - state.lastY) / elapsed;
      state.lastY = event.clientY;
      state.lastTime = now;
      setMobileMapSheetOffset(state.listColumn, state, state.startOffset + (event.clientY - state.startY), false);
      event.preventDefault();
    }

    function end(event) {
      if (!state.dragging || (state.pointerId != null && event.pointerId !== state.pointerId)) return;
      state.dragging = false;
      if (meta.releasePointerCapture && state.pointerId != null) {
        try { meta.releasePointerCapture(state.pointerId); } catch (_) {}
      }
      state.pointerId = null;
      state.detent = releaseMobileMapSheetDetent(state);
      setMobileMapSheetOffset(state.listColumn, state, state.geometry.offsets[state.detent], true);
      state.velocity = 0;
      meta.style.cursor = 'grab';
    }

    meta.addEventListener('pointerdown', begin);
    meta.addEventListener('pointermove', move);
    meta.addEventListener('pointerup', end);
    meta.addEventListener('pointercancel', end);
  }

  function syncMobileMapSheet(root) {
    var workbench = root.querySelector('[data-gtender-public-workbench]');
    var listColumn = root.querySelector('[data-gtender-public-list-column]');
    var previewColumn = root.querySelector('[data-gtender-public-preview-column]');
    var workspace = root.querySelector('[data-gtender-map-workspace]');
    var stage = workspace && workspace.firstElementChild;
    var summary = root.querySelector('[data-gtender-map-summary]');
    var results = root.querySelector('[data-gtender-public-results]');
    var meta = root.querySelector('[data-gtender-results-meta]');
    if (!workbench || !listColumn || !previewColumn) return;

    var mobileMap = usesMobileFilterListbox() && root.getAttribute('data-gtender-view-mode') === 'map';
    var hasCards = Boolean(results && results.querySelector('.gtender-partner-entry__card'));
    var state = mobileMapSheetState(root);

    if (!mobileMap || !stage || !meta || !hasCards) {
      if (listColumn.parentElement !== workbench) workbench.insertBefore(listColumn, previewColumn);
      clearMobileMapSheetStyles(listColumn, meta, summary);
      if (mobileMap && !hasCards) listColumn.style.display = 'none';
      state.dragging = false;
      state.pointerId = null;
      state.geometry = null;
      state.offset = 0;
      state.velocity = 0;
      return;
    }

    var mounted = listColumn.parentElement !== stage;
    if (mounted) {
      stage.appendChild(listColumn);
      state.detent = 'peek';
    }

    listColumn.style.display = 'block';
    listColumn.style.position = 'absolute';
    listColumn.style.inset = 'auto 10px 0 10px';
    listColumn.style.zIndex = '40';
    listColumn.style.boxSizing = 'border-box';
    listColumn.style.width = 'auto';
    listColumn.style.margin = '0';
    listColumn.style.padding = '0 0 max(12px, env(safe-area-inset-bottom, 0px))';
    listColumn.style.overflowY = 'auto';
    listColumn.style.overscrollBehavior = 'contain';
    listColumn.style.webkitOverflowScrolling = 'touch';
    listColumn.style.border = '1px solid rgba(15, 23, 42, .10)';
    listColumn.style.borderRadius = '24px 24px 0 0';
    listColumn.style.background = 'rgba(255, 255, 255, .85)';
    listColumn.style.boxShadow = '0 -12px 30px rgba(15, 23, 42, .14)';
    listColumn.style.backdropFilter = 'blur(20px) saturate(160%)';
    listColumn.style.webkitBackdropFilter = 'blur(20px) saturate(160%)';

    meta.style.position = 'sticky';
    meta.style.top = '0';
    meta.style.zIndex = '2';
    meta.style.margin = '0';
    meta.style.padding = '14px 12px 8px';
    meta.style.background = 'rgba(255, 255, 255, .90)';
    meta.style.backgroundImage = 'linear-gradient(rgba(60, 60, 67, .28), rgba(60, 60, 67, .28))';
    meta.style.backgroundRepeat = 'no-repeat';
    meta.style.backgroundPosition = 'center 6px';
    meta.style.backgroundSize = '36px 5px';
    meta.style.boxShadow = '0 1px 0 rgba(60, 60, 67, .10)';
    meta.style.backdropFilter = 'blur(20px) saturate(160%)';
    meta.style.webkitBackdropFilter = 'blur(20px) saturate(160%)';
    meta.style.touchAction = 'none';
    meta.style.cursor = state.dragging ? 'grabbing' : 'grab';
    meta.style.borderRadius = '24px 24px 0 0';

    if (summary) summary.style.display = 'none';

    state.geometry = mobileMapSheetGeometry(stage);
    listColumn.style.height = state.geometry.fullHeight + 'px';
    listColumn.style.maxHeight = 'none';
    state.offset = state.geometry.offsets[state.detent] == null ? state.geometry.offsets.peek : state.geometry.offsets[state.detent];
    setMobileMapSheetOffset(listColumn, state, state.offset, !mounted);
    bindMobileMapSheet(root, meta, listColumn);
  }

  function bindMobileMapSheetViewport(root) {
    if (!root || root.__gtenderMobileMapSheetViewportBound) return;
    root.__gtenderMobileMapSheetViewportBound = true;
    var pending = false;
    var resync = function () {
      if (pending) return;
      pending = true;
      var run = function () {
        pending = false;
        syncMobileMapSheet(root);
      };
      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(run);
      else window.setTimeout(run, 0);
    };
    window.addEventListener('resize', resync);
    window.addEventListener('orientationchange', resync);
  }

  function polishMobileSurface(root) {
    var mobile = usesMobileFilterListbox();
    var canonicalToolbar = Boolean(root.querySelector('[data-gtender-sort-deadline]'));
    var toolbar = root.querySelector('.gtender-ios-chips');
    var filterChip = root.querySelector('[data-gtender-ios-chip="all"]');
    var filterLabel = filterChip ? filterChip.querySelector('.gtender-ios-chip__label') : null;
    var viewControls = root.querySelector('[data-gtender-view-controls]');
    var closingChip = root.querySelector('[data-gtender-ios-chip="closing"]');
    var closingSentinel = Boolean(closingChip && closingChip.hasAttribute('data-c21-legacy-view-menu-suppressed'));
    var closingInView = Boolean(closingChip && viewControls && closingChip.parentElement === viewControls);
    var viewToggles = viewControls ? viewControls.querySelectorAll('[data-gtender-view-toggle]') : [];
    var breadcrumbs = document.querySelector('[data-global-breadcrumbs]');
    var greeting = document.querySelector('#MainContent > .site-page-heading .gtender-page-greeting');
    var heading = greeting && greeting.parentElement;

    if (!mobile || canonicalToolbar) {
      if (toolbar) {
        ['display', 'grid-template-columns', 'align-items', 'justify-content', 'gap', 'overflow'].forEach(function (name) { toolbar.style.removeProperty(name); });
      }
      if (filterChip) {
        ['grid-column', 'grid-row', 'width', 'min-width', 'height', 'min-height', 'padding', 'border-radius', 'font-size', 'line-height', 'gap', 'justify-content', 'align-items'].forEach(function (name) { filterChip.style.removeProperty(name); });
      }
      if (filterLabel) filterLabel.style.removeProperty('display');
      if (closingChip) {
        if (closingSentinel) {
          closingChip.hidden = true;
          closingChip.setAttribute('aria-hidden', 'true');
          closingChip.removeAttribute('data-gtender-ios-chip');
          closingChip.removeAttribute('role');
          closingChip.removeAttribute('tabindex');
          closingChip.removeAttribute('aria-label');
          closingChip.removeAttribute('aria-pressed');
          closingChip.removeAttribute('title');
        }
        ['display', 'place-items', 'grid-column', 'grid-row', 'width', 'min-width', 'height', 'min-height', 'margin', 'padding', 'border', 'border-color', 'border-radius', 'background', 'background-color', 'background-image', 'background-repeat', 'background-position', 'background-size', 'color', 'cursor', 'box-shadow'].forEach(function (name) { closingChip.style.removeProperty(name); });
      }
      if (viewControls) {
        ['display', 'grid-template-columns', 'grid-column', 'grid-row', 'justify-self', 'gap', 'width', 'min-width', 'max-width', 'height', 'padding', 'border', 'border-radius', 'background', 'box-shadow'].forEach(function (name) { viewControls.style.removeProperty(name); });
      }
      Array.prototype.forEach.call(viewToggles, function (toggle) {
        ['grid-column', 'grid-row', 'width', 'min-width', 'height', 'min-height', 'padding', 'border', 'border-radius', 'background', 'background-image', 'background-repeat', 'background-position', 'background-size', 'color', 'box-shadow', 'font-size', 'line-height', 'gap', 'justify-content', 'align-items'].forEach(function (name) { toggle.style.removeProperty(name); });
        var label = toggle.querySelector('.gtender-ios-chip__label');
        if (label) label.style.removeProperty('display');
      });
      if (breadcrumbs) breadcrumbs.style.removeProperty('margin-bottom');
      if (heading) {
        ['outline', 'outline-offset', 'padding-block', 'margin-block', 'min-height', 'height', 'overflow'].forEach(function (name) { heading.style.removeProperty(name); });
      }
      if (greeting) {
        ['font-size', 'line-height', 'letter-spacing', 'margin-top', 'margin-bottom', 'outline', 'outline-offset', 'position', 'inline-size', 'block-size', 'padding', 'margin', 'overflow', 'clip-path', 'white-space'].forEach(function (name) { greeting.style.removeProperty(name); });
      }
      if (mobile) polishMobileHeading(breadcrumbs, heading, greeting);
      syncMobileMapSheet(root);
      return;
    }

    if (toolbar) {
      toolbar.style.display = 'grid';
      toolbar.style.gridTemplateColumns = closingChip && !closingInView ? '44px 44px minmax(0, 1fr)' : '44px minmax(0, 1fr)';
      toolbar.style.alignItems = 'center';
      toolbar.style.justifyContent = 'space-between';
      toolbar.style.gap = '8px';
      toolbar.style.overflow = 'visible';
    }
    if (filterChip) {
      filterChip.style.gridColumn = '1';
      filterChip.style.gridRow = '1';
      filterChip.style.width = '44px';
      filterChip.style.minWidth = '44px';
      filterChip.style.height = '44px';
      filterChip.style.minHeight = '44px';
      filterChip.style.padding = '0';
      filterChip.style.borderRadius = '10px';
      filterChip.style.fontSize = '0';
      filterChip.style.lineHeight = '0';
      filterChip.style.gap = '0';
      filterChip.style.justifyContent = 'center';
      filterChip.style.alignItems = 'center';
    }
    if (filterLabel) filterLabel.style.display = 'none';
    if (closingChip) {
      if (closingSentinel) {
        closingChip.hidden = false;
        closingChip.removeAttribute('aria-hidden');
        closingChip.setAttribute('data-gtender-ios-chip', 'closing');
        closingChip.setAttribute('role', 'button');
        closingChip.setAttribute('tabindex', '0');
        closingChip.setAttribute('aria-label', isZh(root) ? '即將截止' : 'Closing soon');
        closingChip.title = isZh(root) ? '即將截止' : 'Closing soon';
        if (!closingChip.__gtenderClosingQuickBound) {
          closingChip.addEventListener('keydown', function (event) {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            closingChip.click();
          });
          closingChip.__gtenderClosingQuickBound = true;
        }
      }
      var closingSelected = closingChip.getAttribute('aria-pressed') === 'true';
      closingChip.style.display = 'grid';
      closingChip.style.placeItems = 'center';
      closingChip.style.gridColumn = closingInView ? '1' : '2';
      closingChip.style.gridRow = '1';
      closingChip.style.width = '44px';
      closingChip.style.minWidth = '44px';
      closingChip.style.height = '44px';
      closingChip.style.minHeight = '44px';
      closingChip.style.margin = '0';
      closingChip.style.padding = '0';
      closingChip.style.border = '1px solid ' + (closingSelected ? 'rgba(0, 113, 227, 0.28)' : 'rgba(60, 60, 67, 0.08)');
      closingChip.style.borderRadius = '10px';
      closingChip.style.background = closingSelected ? 'rgba(0, 113, 227, 0.12)' : 'rgba(120, 120, 128, 0.10)';
      closingChip.style.backgroundImage = 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%230071e3%22 stroke-width=%221.7%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Ccircle cx=%2212%22 cy=%2212%22 r=%229%22/%3E%3Cpolyline points=%2212 7 12 12 15.5 14%22/%3E%3C/svg%3E")';
      closingChip.style.backgroundRepeat = 'no-repeat';
      closingChip.style.backgroundPosition = 'center';
      closingChip.style.backgroundSize = '20px 20px';
      closingChip.style.color = '#174479';
      closingChip.style.cursor = 'pointer';
      closingChip.style.boxShadow = 'none';
    }
    if (viewControls) {
      viewControls.style.display = 'grid';
      viewControls.style.gridTemplateColumns = closingInView ? '44px minmax(0, 1fr) 44px 44px' : 'repeat(2, 44px)';
      viewControls.style.gridColumn = closingChip && !closingInView ? '3' : '2';
      viewControls.style.gridRow = '1';
      viewControls.style.justifySelf = 'end';
      viewControls.style.gap = closingInView ? '8px' : '0';
      viewControls.style.width = closingInView ? '100%' : 'calc(var(--gtr-touch-height, 44px) * 2)';
      viewControls.style.minWidth = '0';
      viewControls.style.maxWidth = 'none';
      viewControls.style.height = '44px';
      viewControls.style.padding = '0';
      viewControls.style.border = '0';
      viewControls.style.borderRadius = '0';
      viewControls.style.background = 'transparent';
      viewControls.style.boxShadow = 'none';
    }
    Array.prototype.forEach.call(viewToggles, function (toggle, index) {
      var selected = toggle.getAttribute('aria-pressed') === 'true';
      var label = toggle.querySelector('.gtender-ios-chip__label');
      if (label) label.style.display = 'none';
      toggle.setAttribute('aria-label', isZh(root) ? (index === 0 ? '列表' : '地圖') : (index === 0 ? 'List' : 'Map'));
      if (closingInView) toggle.style.gridColumn = String(index + 3);
      else toggle.style.removeProperty('grid-column');
      toggle.style.gridRow = '1';
      toggle.style.width = '44px';
      toggle.style.minWidth = '44px';
      toggle.style.height = '44px';
      toggle.style.minHeight = '44px';
      toggle.style.padding = '0';
      toggle.style.border = '0';
      toggle.style.borderRadius = '10px';
      toggle.style.background = selected ? '#174479' : 'rgba(120, 120, 128, 0.10)';
      toggle.style.color = selected ? '#fff' : 'var(--gtender-ink, #1d1d1f)';
      toggle.style.boxShadow = selected ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none';
      toggle.style.fontSize = '0';
      toggle.style.lineHeight = '0';
      toggle.style.gap = '0';
      toggle.style.justifyContent = 'center';
      toggle.style.alignItems = 'center';
      if (index === 0) {
        toggle.style.backgroundImage = selected
          ? 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23ffffff%22 stroke-width=%221.8%22 stroke-linecap=%22round%22%3E%3Cpath d=%22M5 7h14M5 12h14M5 17h14%22/%3E%3C/svg%3E")'
          : 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%231d1d1f%22 stroke-width=%221.8%22 stroke-linecap=%22round%22%3E%3Cpath d=%22M5 7h14M5 12h14M5 17h14%22/%3E%3C/svg%3E")';
        toggle.style.backgroundRepeat = 'no-repeat';
        toggle.style.backgroundPosition = 'center';
        toggle.style.backgroundSize = '22px 22px';
      } else {
        toggle.style.removeProperty('background-image');
        toggle.style.removeProperty('background-repeat');
        toggle.style.removeProperty('background-position');
        toggle.style.removeProperty('background-size');
      }
    });
    polishMobileHeading(breadcrumbs, heading, greeting);
    syncMobileMapSheet(root);
  }

  function polishMobileHeading(breadcrumbs, heading, greeting) {
    if (breadcrumbs) breadcrumbs.style.marginBottom = '8px';
    if (heading) {
      heading.style.paddingBlock = '0';
      heading.style.marginBlock = '0';
      heading.style.minHeight = '0';
      heading.style.height = '0';
      heading.style.overflow = 'hidden';
      heading.style.setProperty('outline', '0', 'important');
      heading.style.setProperty('outline-offset', '0', 'important');
    }
    if (greeting) {
      greeting.style.position = 'absolute';
      greeting.style.inlineSize = '1px';
      greeting.style.blockSize = '1px';
      greeting.style.padding = '0';
      greeting.style.margin = '-1px';
      greeting.style.overflow = 'hidden';
      greeting.style.clipPath = 'inset(50%)';
      greeting.style.whiteSpace = 'nowrap';
      greeting.style.setProperty('outline', '0', 'important');
      greeting.style.setProperty('outline-offset', '0', 'important');
    }
  }

  function ensureResultsMeta(root) {
    var column = root.querySelector('[data-gtender-public-results-column]');
    var results = root.querySelector('[data-gtender-public-results]');
    if (!column || !results) return null;
    var meta = column.querySelector('[data-gtender-results-meta]');
    if (meta) return meta;

    meta = document.createElement('div');
    meta.className = 'gtender-results-meta';
    meta.setAttribute('data-gtender-results-meta', '');
    meta.hidden = true;
    meta.style.display = 'none';
    meta.style.flexDirection = 'column';
    meta.style.gap = '8px';
    meta.style.margin = '0 0 12px';
    meta.style.minWidth = '0';

    var visible = document.createElement('p');
    visible.setAttribute('data-gtender-visible-result-count', '');
    visible.setAttribute('aria-live', 'polite');
    visible.style.margin = '0';
    visible.style.color = 'var(--gtender-muted, #64748b)';
    visible.style.fontSize = '13px';
    visible.style.fontWeight = '650';
    visible.style.minWidth = '0';
    visible.style.overflow = 'hidden';
    visible.style.textOverflow = 'ellipsis';
    visible.style.whiteSpace = 'nowrap';
    const resultHeader = document.createElement('div');
    resultHeader.setAttribute('data-gtender-results-header', '');
    resultHeader.style.display = 'flex';
    resultHeader.style.alignItems = 'center';
    resultHeader.style.justifyContent = 'space-between';
    resultHeader.style.flexWrap = 'nowrap';
    resultHeader.style.gap = '8px';
    resultHeader.style.width = '100%';
    resultHeader.style.height = 'var(--gtr-touch-height, 44px)';
    resultHeader.style.minWidth = '0';

    visible.style.flex = '1 1 auto';
    resultHeader.appendChild(visible);

    const exportButton = root.querySelector('[data-gtender-export-csv]');
    if (exportButton) {
      exportButton.style.flex = '0 0 auto';
      exportButton.style.width = 'auto';
      exportButton.style.minHeight = '44px';
      exportButton.style.marginInlineStart = 'auto';
      exportButton.style.fontSize = '13px';
      exportButton.style.whiteSpace = 'nowrap';
      resultHeader.appendChild(exportButton);
    }

    meta.appendChild(resultHeader);

    var chips = document.createElement('div');
    chips.setAttribute('data-gtender-active-filters', '');
    chips.setAttribute('role', 'group');
    chips.setAttribute('aria-label', isZh(root) ? '已套用篩選' : 'Active filters');
    chips.hidden = true;
    chips.style.display = 'none';
    chips.style.flexWrap = 'wrap';
    chips.style.gap = '8px';
    meta.appendChild(chips);

    meta.addEventListener('click', function (event) {
      var remove = event.target && event.target.closest ? event.target.closest('[data-gtender-active-filter-remove]') : null;
      if (!remove || !meta.contains(remove)) return;
      var key = clean(remove.getAttribute('data-gtender-active-filter-remove'));
      if (key === 'published') {
        var dateClear = root.querySelector('[data-gtender-published-range-clear]');
        if (dateClear) dateClear.click();
        return;
      }
      var control = root.querySelector('[data-gtender-search-' + key + ']');
      if (!control) return;
      if (key === 'status') root.removeAttribute('data-gtender-closing-quick');
      control.value = '';
      control.dispatchEvent(new Event('change', { bubbles: true }));
    });

    results.parentNode.insertBefore(meta, results);
    return meta;
  }

  function syncFilterFooter(root) {
    const reset = root.querySelector('[data-gtender-search-clear]');
    const done = root.querySelector('[data-gtender-filter-close].gtender-filter-sheet__done');
    var zhHans = /^zh-CN$/i.test(clean(root.getAttribute('data-locale')));
    if (reset) reset.textContent = zhHans ? '清除筛选' : (isZh(root) ? '清除篩選' : 'Clear filters');
    if (!done) return;

    var results = root.querySelector('[data-gtender-public-results]');
    var failed = Boolean(results && results.querySelector('.gtender-public-error'));
    var visible = results ? results.querySelectorAll('.gtender-partner-entry__card').length : 0;
    var previous = root.querySelector('[data-gtender-page-previous]');
    var next = root.querySelector('[data-gtender-page-next]');
    var hasOtherPages = Boolean((previous && !previous.disabled) || (next && !next.disabled));

    if (failed || visible === 0) {
      done.textContent = zhHans ? '查看结果' : (isZh(root) ? '查看結果' : 'View results');
      return;
    }
    var amount = String(visible) + (hasOtherPages ? '+' : '');
    done.textContent = zhHans ? '查看 ' + amount + ' 个结果' : (isZh(root) ? '查看 ' + amount + ' 個結果' : 'View ' + amount + ' results');
  }

  function syncResultsMeta(root) {
    syncFilterFooter(root);
    var meta = ensureResultsMeta(root);
    if (!meta) return;
    var desktop = usesDesktopFilterSidebar();
    meta.hidden = false;
    meta.style.display = 'flex';

    var results = root.querySelector('[data-gtender-public-results]');
    var visibleCount = results ? results.querySelectorAll('.gtender-partner-entry__card').length : 0;
    var count = meta.querySelector('[data-gtender-visible-result-count]');
    if (count) {
      count.hidden = visibleCount === 0;
      var zhHans = /^zh-CN$/i.test(clean(root.getAttribute('data-locale')));
      count.textContent = zhHans ? '显示 ' + visibleCount + ' 个结果' : (isZh(root) ? '顯示 ' + visibleCount + ' 個結果' : 'Showing ' + visibleCount + ' results');
    }

    var chips = meta.querySelector('[data-gtender-active-filters]');
    if (!desktop) {
      if (chips) {
        chips.hidden = true;
        chips.style.display = 'none';
      }
      return;
    }
    if (!chips) return;
    chips.replaceChildren();
    var entries = activeStructuredFilters(root);
    chips.hidden = entries.length === 0;
    chips.style.display = entries.length ? 'flex' : 'none';
    entries.forEach(function (entry) {
      var button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('data-gtender-active-filter-remove', entry.key);
      button.setAttribute('aria-label', (isZh(root) ? '移除篩選：' : 'Remove filter: ') + entry.label);
      button.textContent = entry.label + ' ×';
      button.style.minHeight = '36px';
      button.style.maxWidth = '100%';
      button.style.padding = '7px 10px';
      button.style.border = '1px solid color-mix(in srgb, var(--lg-form-accent, #174479) 28%, transparent)';
      button.style.borderRadius = '999px';
      button.style.background = 'color-mix(in srgb, var(--lg-form-accent, #174479) 9%, transparent)';
      button.style.color = 'var(--lg-form-accent, #174479)';
      button.style.font = 'inherit';
      button.style.fontSize = '12px';
      button.style.fontWeight = '650';
      button.style.lineHeight = '1.2';
      button.style.overflow = 'hidden';
      button.style.textOverflow = 'ellipsis';
      button.style.whiteSpace = 'nowrap';
      button.style.cursor = 'pointer';
      chips.appendChild(button);
    });
  }

  function syncViewMenu(root) {
    var menu = root.querySelector('[data-gtender-view-menu]');
    if (!menu) return;
    var mode = clean(root.getAttribute('data-gtender-view-mode')) || 'grid';
    menu.querySelectorAll('[data-gtender-view-select]').forEach(function (button) {
      var selected = button.getAttribute('data-gtender-view-select') === mode;
      button.setAttribute('aria-checked', selected ? 'true' : 'false');
      button.style.background = selected ? 'color-mix(in srgb, var(--lg-form-accent, #174479) 10%, transparent)' : 'transparent';
      button.style.color = selected ? 'var(--lg-form-accent, #174479)' : 'var(--gtender-ink, #1c1c1e)';
    });
  }


  function stripVisibleClock(value) {
    var raw = clean(value);
    if (!raw) return '';
    return raw.replace(/(?:,\s*|\s+)\d{1,2}:\d{2}(?::\d{2})?(?:\s*(?:HKT|HKST))?\s*$/i, '').trim();
  }

  function canonicalVisibleDate(value) {
    var raw = stripVisibleClock(clean(value).replace(/^(?:Closing|截標|截止)\s*[:：]\s*/i, '')).replace(/\s*\([A-Za-z]{3,9}\)\s*$/, '').trim();
    if (!raw) return '';
    var ymd = raw.match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
    if (ymd) return two(ymd[3]) + '/' + two(ymd[2]) + '/' + ymd[1];
    var dmy = raw.match(/^(\d{1,2})[\/](\d{1,2})[\/](\d{4})$/);
    if (dmy) return two(dmy[1]) + '/' + two(dmy[2]) + '/' + dmy[3];
    var named = raw.match(/^(\d{1,2})[-\s]([A-Za-z]{3})[-\s](\d{4})$/);
    if (named && Object.prototype.hasOwnProperty.call(MONTHS, named[2].toLowerCase())) return two(named[1]) + '/' + two(MONTHS[named[2].toLowerCase()]) + '/' + named[3];
    return raw;
  }

  function normalizeVisibleDates(root) {
    var nodes = [];
    var detail = root.querySelector('[data-gtender-detail-deadline]');
    var published = root.querySelector('[data-gtender-detail-published]');
    if (detail) nodes.push(detail);
    if (published) nodes.push(published);
    root.querySelectorAll('.gtender-card__date').forEach(function (node) { nodes.push(node); });
    nodes.forEach(function (node) {
      var raw = clean(node.textContent);
      if (!raw || raw === 'Not provided' || raw === '未提供') return;
      var visible = canonicalVisibleDate(raw);
      if (visible && visible !== raw) node.textContent = visible;
    });
  }

  function normalizeDetailProvenance(root) {
    var dialog = root.querySelector('[data-gtender-public-detail]');
    if (!dialog) return;
    var source = dialog.querySelector('[data-gtender-detail-source]');
    var verified = dialog.querySelector('[data-gtender-detail-verified]');
    var official = dialog.querySelector('[data-gtender-detail-official]');
    if (!source || !verified || !official) return;
    var sourceLabel = source.previousElementSibling;
    var verifiedLabel = verified.previousElementSibling;
    if (sourceLabel && sourceLabel.hidden) sourceLabel.hidden = false;
    if (verifiedLabel && verifiedLabel.hidden) verifiedLabel.hidden = false;
    // Public detail runtime owns the official link and its accessible source name.
    // Preserve the single anchor in the sticky toolbar; provenance shows source text.
    // Do not reparent or relabel the official action from this iOS projection.
  }

  function syncClosingChip(root) {
    var status = root.querySelector('[data-gtender-search-status]');
    var closingChip = root.querySelector('[data-gtender-ios-chip="closing"]');
    var closingActive = Boolean(status && status.value === 'closing');
    if (closingChip) closingChip.setAttribute('aria-pressed', closingActive ? 'true' : 'false');
    applyChipActiveStyle(closingChip, closingActive);
    if (closingChip && closingChip.hasAttribute('data-c21-legacy-view-menu-suppressed')) {
      closingChip.style.background = closingActive ? 'rgba(0, 113, 227, 0.12)' : 'rgba(120, 120, 128, 0.10)';
      closingChip.style.borderColor = closingActive ? 'rgba(0, 113, 227, 0.28)' : 'rgba(60, 60, 67, 0.08)';
      closingChip.style.backgroundImage = 'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%230071e3%22 stroke-width=%221.7%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Ccircle cx=%2212%22 cy=%2212%22 r=%229%22/%3E%3Cpolyline points=%2212 7 12 12 15.5 14%22/%3E%3C/svg%3E")';
      closingChip.style.backgroundRepeat = 'no-repeat';
      closingChip.style.backgroundPosition = 'center';
      closingChip.style.backgroundSize = '20px 20px';
      closingChip.style.color = '#174479';
    }

    var quickClosing = root.getAttribute('data-gtender-closing-quick') === 'true' && closingActive;
    var activeCount = ['category', 'department', 'status', 'deadline', 'institution-type', 'source'].filter(function (key) {
      var control = root.querySelector('[data-gtender-search-' + key + ']');
      if (!control || !clean(control.value)) return false;
      if (key === 'status' && quickClosing) return false;
      return true;
    }).length;

    var publishedFrom = clean(root.getAttribute('data-gtender-published-from'));
    var publishedTo = clean(root.getAttribute('data-gtender-published-to'));
    if (publishedFrom && publishedTo) activeCount += 1;

    var filterChip = root.querySelector('[data-gtender-ios-chip="all"]');
    if (filterChip) {
      filterChip.setAttribute('aria-pressed', activeCount > 0 ? 'true' : 'false');
      filterChip.setAttribute('data-active-filter-count', String(activeCount));
      filterChip.setAttribute('aria-label', isZh(root)
        ? (activeCount > 0 ? '篩選，已套用 ' + activeCount + ' 個條件' : '篩選')
        : (activeCount > 0 ? 'Filters, ' + activeCount + ' active' : 'Filters'));
    }
    applyChipActiveStyle(filterChip, activeCount > 0);

    var count = root.querySelector('[data-gtender-filter-count]');
    if (count) count.textContent = String(activeCount);
    var countLabel = root.querySelector('[data-gtender-filter-count-label]');
    if (countLabel) countLabel.hidden = activeCount === 0;
    applyFilterBadgeStyle(filterChip, countLabel, count, activeCount);
    syncResultsMeta(root);
  }

  function rememberFilterOpener(root, opener) {
    var candidate = opener && typeof opener.focus === 'function' ? opener : document.activeElement;
    if (candidate && candidate !== document.body && root.contains(candidate)) filterOpenerByRoot.set(root, candidate);
  }

  function restoreFilterOpener(root) {
    var opener = filterOpenerByRoot.get(root);
    filterOpenerByRoot.delete(root);
    if (!opener || !opener.isConnected || typeof opener.focus !== 'function') return;
    window.setTimeout(function () { try { opener.focus({ preventScroll: true }); } catch (_) { try { opener.focus(); } catch (_) {} } }, 0);
  }

  function filterFocusableNodes(sheet) {
    return Array.prototype.filter.call(sheet.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'), function (node) {
      if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      if (usesMobileFilterListbox() && node.classList && node.classList.contains('gtender-mobile-select-native')) return false;
      return true;
    });
  }

  function ensureFilterSheetHandle(sheet) {
    if (!sheet) return;
    var shell = sheet.querySelector('.gtender-filter-sheet__shell');
    if (!shell || shell.querySelector('[data-gtender-filter-sheet-handle]')) return;
    var handle = document.createElement('div');
    handle.className = 'gtender-filter-sheet__handle';
    handle.setAttribute('data-gtender-filter-sheet-handle', '');
    handle.setAttribute('aria-hidden', 'true');
    var grip = document.createElement('span');
    grip.setAttribute('aria-hidden', 'true');
    handle.appendChild(grip);
    shell.insertBefore(handle, shell.firstChild);
  }

  function syncFilterPresentation(root) {
    var sheet = root.querySelector('[data-gtender-filter-sheet]');
    if (!sheet) return;

    const desktop = usesDesktopFilterSidebar();
    const filterTrigger = root.querySelector(
      '[data-gtender-ios-chip="all"]'
    );

    if (filterTrigger) {
      filterTrigger.hidden = desktop;

      if (desktop) {
        filterTrigger.setAttribute('aria-hidden', 'true');
      } else {
        filterTrigger.removeAttribute('aria-hidden');
        filterTrigger.style.removeProperty('display');
      }
    }

    const reset = sheet.querySelector('[data-gtender-search-clear]');
    const header = sheet.querySelector('.gtender-filter-sheet__header');
    const footer = sheet.querySelector('.gtender-filter-sheet__actions');
    const close = header && header.querySelector('[data-gtender-filter-close]');

    if (reset && header && footer) {
      if (desktop) {
        if (reset.parentElement !== header) {
          header.insertBefore(reset, close);
        }
        reset.style.removeProperty('color');
        reset.style.removeProperty('border-color');
      } else if (reset.parentElement !== footer) {
        footer.insertBefore(reset, footer.firstChild);
      }
    }

    if (desktop) {
      sheet.setAttribute('data-gtender-filter-presentation', 'sidebar');
      var isModal = false;
      try { isModal = Boolean(sheet.matches && sheet.matches(':modal')); } catch (_) {}
      if (isModal && sheet.open && typeof sheet.close === 'function') { try { sheet.close(); } catch (_) { sheet.removeAttribute('open'); } return; }
      if (!sheet.open) sheet.setAttribute('open', '');
      syncResultsMeta(root);
      return;
    }
    if (sheet.getAttribute('data-gtender-filter-presentation') === 'sidebar') {
      sheet.removeAttribute('data-gtender-filter-presentation');

      if (sheet.open && typeof sheet.close === 'function') {
        root.__gtenderFilterTransitionCloses =
          (root.__gtenderFilterTransitionCloses || 0) + 1;

        try {
          sheet.close();
        } catch (_) {
          root.__gtenderFilterTransitionCloses -= 1;
          sheet.removeAttribute('open');
        }
      } else {
        sheet.removeAttribute('open');
      }
    }
    syncResultsMeta(root);
  }

  function openFilterSheet(root, focusSelector, opener) {
    const sheet = root.querySelector('[data-gtender-filter-sheet]');
    if (!sheet) return;

    if (usesDesktopFilterSidebar()) {
      syncFilterPresentation(root);

      if (focusSelector) {
        const desktopControl = sheet.querySelector(focusSelector);
        if (desktopControl) {
          window.setTimeout(() => {
            try { desktopControl.focus(); } catch (_) {}
          }, 0);
        }
      }
      return;
    }

    rememberFilterOpener(root, opener);

    if (typeof sheet.showModal !== 'function') {
      sheet.setAttribute('open', '');

      if (focusSelector) {
        const fallbackControl = sheet.querySelector(focusSelector);
        if (fallbackControl) {
          window.setTimeout(function () {
            try { fallbackControl.focus(); } catch (_) {}
          }, 0);
        }
      }

      return;
    }

    let isModal = false;
    try { isModal = sheet.matches(':modal'); } catch (_) {}

    if (!isModal) {
      if (sheet.open) {
        root.__gtenderFilterTransitionCloses =
          (root.__gtenderFilterTransitionCloses || 0) + 1;

        try {
          sheet.close();
        } catch (error) {
          root.__gtenderFilterTransitionCloses -= 1;
          console.error('GTender filter dialog could not reset.', error);
          return;
        }
      }

      try {
        sheet.showModal();
      } catch (error) {
        console.error('GTender filter dialog could not open.', error);
        restoreFilterOpener(root);
        return;
      }
    }

    if (focusSelector) {
      const control = sheet.querySelector(focusSelector);
      if (control) {
        window.setTimeout(() => {
          if (!sheet.open || usesDesktopFilterSidebar()) return;
          try { control.focus(); } catch (_) {}
        }, 0);
      }
    }
  }

  function closeFilterSheet(root) {
    if (usesDesktopFilterSidebar()) return;
    var sheet = root.querySelector('[data-gtender-filter-sheet]');
    if (!sheet) return;
    var nativeClose = false;
    try { if (typeof sheet.close === 'function' && sheet.open) { nativeClose = true; sheet.close(); } else sheet.removeAttribute('open'); }
    catch (_) { sheet.removeAttribute('open'); }
    if (!nativeClose) restoreFilterOpener(root);
  }

  function activateFilter(root, kind, opener) {
    if (kind === 'all') { openFilterSheet(root, usesMobileFilterListbox() ? '[data-gtender-mobile-select-trigger]' : 'select', opener); return; }
    if (kind === 'closing') {
      var status = root.querySelector('[data-gtender-search-status]');
      if (!status) return;
      var enable = status.value !== 'closing';
      status.value = enable ? 'closing' : '';
      if (enable) root.setAttribute('data-gtender-closing-quick', 'true');
      else root.removeAttribute('data-gtender-closing-quick');
      syncClosingChip(root);
      root.__gtenderClosingQuickDispatch = true;
      status.dispatchEvent(new Event('change', { bubbles: true }));
      root.__gtenderClosingQuickDispatch = false;
    }
  }

  function enhanceCard(root, card) {
    if (!card) return;
    var detail = card.querySelector('[data-gtender-detail-open]');
    if (!detail) return;
    var firstPass = card.getAttribute('data-gtender-ios-card') !== VERSION;
    if (firstPass) {
      card.setAttribute('data-gtender-ios-card', VERSION);
      card.removeAttribute('role'); card.removeAttribute('tabindex'); card.removeAttribute('aria-label');
      detail.removeAttribute('aria-hidden'); detail.removeAttribute('tabindex');
      card.addEventListener('click', function (event) {
        var interactive = event.target && event.target.closest ? event.target.closest('a,button,input,select,textarea,summary') : null;
        if (interactive) return;
        detail.click();
      });
    }

    var mobile = usesMobileFilterListbox();
    var header = card.querySelector('.gtender-card__header-row');
    var badges = card.querySelectorAll('.gtender-card__badge');
    var title = card.querySelector('.gtender-card__title');
    var share = card.querySelector('.gtender-card__share--visible');
    var bookmark = card.querySelector('.gtender-card-bookmark-btn');
    var timing = card.querySelector('[data-gtender-card-timing]');

    if (mobile) {
      card.style.setProperty('border-color', 'rgba(0, 0, 0, 0.06)', 'important');
      card.style.setProperty('box-shadow', '0 2px 8px rgba(0, 0, 0, 0.04)', 'important');
      if (header) {
        ['min-height', 'align-items', 'padding-inline-end'].forEach(function (property) {
          header.style.removeProperty(property);
        });
      }
      Array.prototype.forEach.call(badges, function (badge) {
        [
          'min-height',
          'padding',
          'border-radius',
          'font-size',
          'font-weight',
          'line-height'
        ].forEach(function (property) {
          badge.style.removeProperty(property);
        });
      });
      if (title) {
        title.style.setProperty('line-height', '1.38', 'important');
        title.style.setProperty('letter-spacing', '-0.006em', 'important');
        title.style.setProperty('margin-top', '2px', 'important');
        title.style.setProperty('padding-inline-end', '0', 'important');
      }
      if (share) {
        share.style.setProperty('top', '20px', 'important');
        share.style.setProperty('inset-inline-end', '58px', 'important');
      }
      if (bookmark) {
        bookmark.style.setProperty('top', '20px', 'important');
        bookmark.style.setProperty('inset-inline-end', '10px', 'important');
      }
    } else {
      card.style.removeProperty('border-color');
      card.style.removeProperty('box-shadow');
      if (header) {
        header.style.removeProperty('min-height');
        header.style.removeProperty('align-items');
        header.style.removeProperty('padding-inline-end');
      }
      Array.prototype.forEach.call(badges, function (badge) {
        ['min-height', 'padding', 'border-radius', 'font-size', 'font-weight', 'line-height'].forEach(function (name) { badge.style.removeProperty(name); });
      });
      if (title) {
        ['line-height', 'letter-spacing', 'margin-top', 'padding-inline-end'].forEach(function (name) { title.style.removeProperty(name); });
      }
      if (share) { share.style.removeProperty('top'); share.style.removeProperty('inset-inline-end'); }
      if (bookmark) { bookmark.style.removeProperty('top'); bookmark.style.removeProperty('inset-inline-end'); }
    }

    if (timing) {
      timing.style.display = 'flex'; timing.style.alignItems = 'center'; timing.style.justifyContent = 'space-between'; timing.style.gap = '8px'; timing.style.width = '100%'; timing.style.minWidth = '0'; timing.style.minHeight = mobile ? '26px' : '28px'; timing.style.marginTop = mobile ? '4px' : 'auto';
      timing.style.paddingTop = mobile ? '8px' : '';
      timing.style.borderTop = mobile ? '1px solid rgba(60, 60, 67, 0.08)' : '';
      var deadlineBadge = timing.querySelector('.gtender-card__badge--deadline');
      if (deadlineBadge) { deadlineBadge.style.flex = '0 0 auto'; deadlineBadge.style.margin = '0'; deadlineBadge.style.minHeight = mobile ? '24px' : '26px'; deadlineBadge.style.padding = mobile ? '0 6px' : '0 7px'; deadlineBadge.style.fontSize = '0.75rem'; }
      var deadlineDate = timing.querySelector('.gtender-card__date');
      if (deadlineDate) {
        var compactDate = canonicalVisibleDate(deadlineDate.textContent);
        if (compactDate && compactDate !== clean(deadlineDate.textContent)) deadlineDate.textContent = compactDate;
        deadlineDate.style.flex = '0 1 auto'; deadlineDate.style.minWidth = '0'; deadlineDate.style.margin = '0 0 0 auto'; deadlineDate.style.textAlign = 'right'; deadlineDate.style.whiteSpace = 'nowrap'; deadlineDate.style.fontSize = '0.75rem'; deadlineDate.style.lineHeight = '1.2'; deadlineDate.style.color = mobile ? '#8e8e93' : '';
      }
    }
  }

  function enhanceCards(root) {
    root.querySelectorAll('.gtender-partner-entry__card, [data-gtender-public-results] article').forEach(function (card) { enhanceCard(root, card); });
    syncClosingChip(root);
    normalizeVisibleDates(root);
    polishMobileSurface(root);
  }

  function enhanceStarSheet(root) {
    var dialog = root.querySelector('[data-gtender-star-list-dialog]');
    if (!dialog) return;
    dialog.classList.add('gtender-star-sheet');
    dialog.setAttribute('data-gtender-ios-star-sheet', VERSION);
    var shell = dialog.querySelector('.gtender-filter-sheet__shell');
    if (shell && !shell.querySelector('[data-gtender-star-sheet-handle]')) {
      var handle = document.createElement('div'); handle.className = 'gtender-public-detail__handle gtender-star-sheet__handle'; handle.setAttribute('data-gtender-star-sheet-handle', ''); handle.setAttribute('aria-hidden', 'true');
      var grip = document.createElement('span'); grip.setAttribute('aria-hidden', 'true'); handle.appendChild(grip); shell.insertBefore(handle, shell.firstChild);
    }
    var list = dialog.querySelector('[data-gtender-star-list]');
    if (list) { list.classList.add('gtender-star-list'); list.style.display = 'grid'; list.style.gridTemplateColumns = 'minmax(0, 1fr)'; list.style.gap = '10px'; list.style.padding = '2px'; }
    dialog.querySelectorAll('[data-gtender-starred-item]').forEach(function (card) {
      card.classList.add('gtender-star-list__row'); card.style.minHeight = '0'; card.style.padding = '14px 16px'; card.style.gap = '10px'; card.style.cursor = 'default';
      var header = card.querySelector('.gtender-card__header-row'); if (header) { header.style.minHeight = '44px'; header.style.alignItems = 'center'; }
      var title = card.querySelector('.gtender-card__title'); if (title) { title.style.fontSize = '0.9375rem'; title.style.lineHeight = '1.35'; title.style.webkitLineClamp = '2'; }
      var deadline = card.querySelector('.gtender-card__date'); if (deadline) { deadline.style.fontSize = '0.8125rem'; deadline.style.margin = '0'; }
      card.querySelectorAll('.gtender-partner-entry__button').forEach(function (button) { button.style.minHeight = '44px'; });
    });
    normalizeVisibleDates(root);
  }

  /* GTENDER-MOBILE-FILTER-LISTBOX-01
   * Mobile filter selects stay inside the existing filter sheet instead of
   * delegating presentation to the browser's native floating select menu.
   * The original <select> remains the canonical value/change owner.
   */
  function closeMobileFilterListboxes(root, except) {
    root.querySelectorAll('[data-gtender-mobile-select]').forEach(function (host) {
      if (except && host === except) return;
      var trigger = host.querySelector('[data-gtender-mobile-select-trigger]');
      var listbox = host.querySelector('[data-gtender-mobile-select-listbox]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (listbox) listbox.hidden = true;
    });
  }

  function syncMobileFilterSelect(host) {
    if (!host) return;
    var select = host.previousElementSibling;
    var trigger = host.querySelector('[data-gtender-mobile-select-trigger]');
    var listbox = host.querySelector('[data-gtender-mobile-select-listbox]');
    if (!select || select.tagName !== 'SELECT' || !trigger || !listbox) return;

    var selected = select.selectedOptions && select.selectedOptions[0];
    var value = trigger.querySelector('[data-gtender-mobile-select-value]');
    if (value) value.textContent = clean(selected && selected.textContent) || clean(select.value) || '—';

    listbox.replaceChildren();
    Array.prototype.forEach.call(select.options || [], function (option) {
      var item = document.createElement('button');
      item.type = 'button';
      item.className = 'gtender-mobile-select__option';
      item.setAttribute('role', 'option');
      item.setAttribute('data-gtender-mobile-select-option', '');
      item.setAttribute('data-value', clean(option.value));
      item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
      item.tabIndex = option.selected ? 0 : -1;
      item.textContent = clean(option.textContent) || clean(option.value) || '—';
      item.addEventListener('click', function () {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        syncMobileFilterSelect(host);
        trigger.setAttribute('aria-expanded', 'false');
        listbox.hidden = true;
        try { trigger.focus({ preventScroll: true }); } catch (_) { try { trigger.focus(); } catch (_) {} }
      });
      listbox.appendChild(item);
    });
  }

  function openMobileFilterListbox(root, host, focusSelected) {
    if (!usesMobileFilterListbox()) return;
    closeMobileFilterListboxes(root, host);
    var trigger = host.querySelector('[data-gtender-mobile-select-trigger]');
    var listbox = host.querySelector('[data-gtender-mobile-select-listbox]');
    if (!trigger || !listbox) return;
    var willOpen = listbox.hidden;
    trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    listbox.hidden = !willOpen;
    if (willOpen && focusSelected) {
      var option = listbox.querySelector('[aria-selected="true"]') || listbox.querySelector('[data-gtender-mobile-select-option]');
      if (option) window.setTimeout(function () { try { option.focus({ preventScroll: true }); } catch (_) { try { option.focus(); } catch (_) {} } }, 0);
    }
  }

  function enhanceMobileFilterSelect(root, select, index) {
    if (!select || select.getAttribute('data-gtender-mobile-select-bound') === 'true') return;
    var field = select.closest('.gtender-public-search__field');
    if (!field) return;

    select.setAttribute('data-gtender-mobile-select-bound', 'true');
    select.classList.add('gtender-mobile-select-native');

    var host = document.createElement('div');
    host.className = 'gtender-mobile-select';
    host.setAttribute('data-gtender-mobile-select', '');

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'gtender-mobile-select__trigger';
    trigger.setAttribute('data-gtender-mobile-select-trigger', '');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    var value = document.createElement('span');
    value.setAttribute('data-gtender-mobile-select-value', '');
    trigger.appendChild(value);

    var listbox = document.createElement('div');
    listbox.className = 'gtender-mobile-select__listbox';
    listbox.id = (root.id || 'gtender') + '-mobile-filter-' + index;
    listbox.setAttribute('data-gtender-mobile-select-listbox', '');
    listbox.setAttribute('role', 'listbox');
    listbox.hidden = true;
    trigger.setAttribute('aria-controls', listbox.id);

    host.appendChild(trigger);
    host.appendChild(listbox);
    select.insertAdjacentElement('afterend', host);

    trigger.addEventListener('click', function () { openMobileFilterListbox(root, host, false); });
    trigger.addEventListener('keydown', function (event) {
      if (!usesMobileFilterListbox()) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openMobileFilterListbox(root, host, true);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        trigger.setAttribute('aria-expanded', 'false');
        listbox.hidden = true;
      }
    });

    listbox.addEventListener('keydown', function (event) {
      var options = Array.prototype.slice.call(listbox.querySelectorAll('[data-gtender-mobile-select-option]'));
      if (!options.length) return;
      var current = Math.max(0, options.indexOf(document.activeElement));
      var next = current;
      if (event.key === 'ArrowDown') next = Math.min(options.length - 1, current + 1);
      else if (event.key === 'ArrowUp') next = Math.max(0, current - 1);
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = options.length - 1;
      else if (event.key === 'Escape') {
        event.preventDefault();
        trigger.setAttribute('aria-expanded', 'false');
        listbox.hidden = true;
        trigger.focus();
        return;
      } else return;
      event.preventDefault();
      options.forEach(function (option) { option.tabIndex = -1; });
      options[next].tabIndex = 0;
      options[next].focus();
    });

    select.addEventListener('change', function () { syncMobileFilterSelect(host); });
    var optionObserver = new MutationObserver(function () { syncMobileFilterSelect(host); });
    optionObserver.observe(select, { childList: true, subtree: true, characterData: true });
    host.__gtenderMobileSelectObserver = optionObserver;
    syncMobileFilterSelect(host);
  }

  function enhanceMobileFilterSelects(root) {
    var sheet = root.querySelector('[data-gtender-filter-sheet]');
    if (!sheet) return;
    Array.prototype.forEach.call(sheet.querySelectorAll('.gtender-public-search__filter-grid select'), function (select, index) {
      enhanceMobileFilterSelect(root, select, index);
    });
  }

  function bindFilterSheet(root) {
    var sheet = root.querySelector('[data-gtender-filter-sheet]');
    if (!sheet || sheet.getAttribute('data-gtender-filter-sheet-bound') === 'true') return;
    sheet.setAttribute('data-gtender-filter-sheet-bound', 'true');
    ensureFilterSheetHandle(sheet);
    var done = sheet.querySelector('.gtender-filter-sheet__done');
    var resetAction = sheet.querySelector('.gtender-filter-sheet__reset');
    if (done) { done.style.background = 'var(--lg-form-accent, #174479)'; done.style.borderColor = 'var(--lg-form-accent, #174479)'; done.style.color = '#fff'; }
    if (resetAction) { resetAction.style.borderColor = 'var(--lg-form-accent, #174479)'; resetAction.style.color = 'var(--lg-form-accent, #174479)'; }
    syncFilterFooter(root);
    syncFilterPresentation(root);
    if (window.matchMedia) {
      var media = window.matchMedia(DESKTOP_FILTER_QUERY);
      var handlePresentationChange = function () { syncFilterPresentation(root); syncResultsMeta(root); };
      if (typeof media.addEventListener === 'function') media.addEventListener('change', handlePresentationChange); else if (typeof media.addListener === 'function') media.addListener(handlePresentationChange);
      root.__gtenderFilterPresentationMedia = media; root.__gtenderFilterPresentationChange = handlePresentationChange;
    }
    sheet.addEventListener('click', function (event) {
      if (usesDesktopFilterSidebar()) return;
      if (event.target === sheet) closeFilterSheet(root);
      var close = event.target && event.target.closest ? event.target.closest('[data-gtender-filter-close]') : null;
      if (close && sheet.contains(close)) { event.preventDefault(); closeFilterSheet(root); }
    });
    sheet.addEventListener('keydown', function (event) {
      if (usesDesktopFilterSidebar()) return;
      if (event.key === 'Escape') { event.preventDefault(); closeFilterSheet(root); return; }
      if (event.key !== 'Tab') return;
      var nodes = filterFocusableNodes(sheet); if (!nodes.length) return;
      var first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    sheet.addEventListener('close', function () {
      if (root.__gtenderFilterTransitionCloses > 0) {
        root.__gtenderFilterTransitionCloses -= 1;
        return;
      }

      if (usesDesktopFilterSidebar()) {
        sheet.setAttribute('data-gtender-filter-presentation', 'sidebar');
        sheet.setAttribute('open', '');
        return;
      }

      restoreFilterOpener(root);
    });
  }

  function bind(root) {
    if (!root || root.getAttribute('data-gtender-ios-hig-bound') === VERSION) return;
    root.setAttribute('data-gtender-ios-hig-bound', VERSION);
    var toolbar = root.querySelector('.gtender-ios-chips');
    if (toolbar) toolbar.classList.add('lg-form');
    bindMobileMapSheetViewport(root);
    polishMobileSurface(root);

    root.addEventListener('click', function (event) {
      var chip = event.target && event.target.closest ? event.target.closest('[data-gtender-ios-chip]') : null;
      if (!chip || !root.contains(chip)) return;
      var kind = clean(chip.getAttribute('data-gtender-ios-chip'));
      if (kind === 'closing' || kind === 'all') { event.preventDefault(); activateFilter(root, kind, chip); }
    });

    ['category', 'department', 'status', 'deadline', 'institution-type', 'source'].forEach(function (key) {
      var control = root.querySelector('[data-gtender-search-' + key + ']');
      if (!control) return;
      control.addEventListener('change', function () {
        if (key === 'status' && !root.__gtenderClosingQuickDispatch) root.removeAttribute('data-gtender-closing-quick');
        syncClosingChip(root);
      });
    });

    var reset = root.querySelector('[data-gtender-search-clear]');
    if (reset) reset.addEventListener('click', function () { root.removeAttribute('data-gtender-closing-quick'); window.setTimeout(function () { syncClosingChip(root); }, 0); });

    var filterStateObserver = new MutationObserver(function () { syncClosingChip(root); syncViewMenu(root); polishMobileSurface(root); });
    filterStateObserver.observe(root, { attributes: true, attributeFilter: ['data-gtender-published-from', 'data-gtender-published-to', 'data-gtender-view-mode'] });
    root.__gtenderFilterStateObserver = filterStateObserver;

    var search = root.querySelector('[data-gtender-search-query]');
    if (search) { search.setAttribute('enterkeyhint', 'search'); search.setAttribute('inputmode', 'search'); }

    enhanceMobileFilterSelects(root);
    bindFilterSheet(root);
    enhanceCards(root);
    enhanceStarSheet(root);
    normalizeVisibleDates(root);
    normalizeDetailProvenance(root);
    syncClosingChip(root);
    syncResultsMeta(root);
    syncViewMenu(root);
    polishMobileSurface(root);

    var results = root.querySelector('[data-gtender-public-results]');
    if (results) { var observer = new MutationObserver(function () { enhanceCards(root); syncResultsMeta(root); polishMobileSurface(root); }); observer.observe(results, { childList: true, subtree: true }); }
    var starObserver = new MutationObserver(function () { enhanceStarSheet(root); normalizeVisibleDates(root); normalizeDetailProvenance(root); });
    starObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'href'] });
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) bind(target);
    if (target.querySelectorAll) target.querySelectorAll(ROOT_SELECTOR).forEach(bind);
  }

  document.addEventListener('shopify:section:load', function (event) { boot(event.target || document); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  else boot(document);
})();